import { findCustomScreenChildSection } from '@modules/screen-view-v2/customScreenDataSources';
import { createPurchaseCompletion } from '@modules/screen-view-v2/components/screens/WebEmbed/createPurchaseCompletion';
import type { CustomPurchaseSlotMount } from '@modules/screen-view-v2/customPurchaseSlot';
/**
 * @fileoverview Shadow DOM SDK Builder for WebEmbed
 *
 * Builds a full segmentlySDK object for Shadow DOM mode that mirrors
 * the iframe IFRAME_SETUP_SCRIPT API but uses direct host callbacks
 * instead of MessagePort communication.
 *
 * KEY ADVANTAGE: Direct function calls preserve the user gesture context,
 * enabling Apple Pay (paymentRequest.show()) from custom screen buttons.
 * In iframe mode, the postMessage boundary breaks the gesture chain.
 *
 * API surface matches IframeRenderer's IFRAME_SETUP_SCRIPT exactly:
 * - Data methods: screen, getEmail, getVariable, getProducts, etc.
 * - Action methods: navigateNext, trackEvent, purchaseProduct, etc.
 * - Legacy compat: onboardingOnlineCustomScreenData.inputs Proxy
 * - Variable system: getVariable/setVariable/onVariableChange with local snapshot
 */

import type {
  CustomScreenButtonActionRequest,
  CustomScreenOptionActionRequest,
  LegacyInitData,
} from '../../../types/customScreenBridge.types';

// ============================================================================
// Types
// ============================================================================

export interface ShadowDomHostCallbacks {
  onMountPurchaseButton?: CustomPurchaseSlotMount;
  onNavigate: (data: Record<string, unknown>) => void;
  onBack: () => void;
  onTrackEvent: (name: string, params?: Record<string, unknown>) => void;
  onValueChange?: (key: string, value: unknown) => void;
  onSetVariable?: (variableId: string, value: unknown) => void;
  onPurchaseProduct?: (requestId: string, productId: string, childSectionId?: string) => void;
  onSelectProduct?: (childSectionId: string, productIndex: number, productId: string) => void;
  onTriggerOptionAction?: (request: CustomScreenOptionActionRequest) => void;
  onTriggerButtonAction?: (request: CustomScreenButtonActionRequest) => void;
}

export interface ShadowDomSdkInstance {
  _disposePurchaseSlots(): void;
  /** Deliver purchase result from host (called by WebEmbed useEffect on lastPurchaseResult change) */
  _deliverPurchaseResult(requestId: string, status: 'success' | 'cancel' | 'error', error?: string): void;
  /** Deliver variable update from host (called by WebEmbed useEffect on lastVariableUpdate change) */
  _deliverVariableUpdate(variableId: string, value: unknown): void;
  /** The SDK object set on window.segmentlySDK */
  sdk: Record<string, unknown>;
}

// ============================================================================
// Builder
// ============================================================================

export function buildShadowDomSdk(
  initData: LegacyInitData,
  callbacks: ShadowDomHostCallbacks,
  shadowRoot: ShadowRoot,
): ShadowDomSdkInstance {
  // ── State ──────────────────────────────────────────────────
  const variableDefinitions: Record<string, {
    id: string; name: string; type: string;
    options?: Array<{ id: string; label: string }>;
    required?: boolean;
  }> = { ...(initData.variableDefinitions ?? {}) };

  const variableValues: Record<string, unknown> = {};
  if (initData.variableValues) {
    for (const [k, v] of Object.entries(initData.variableValues)) {
      if (v !== undefined) variableValues[k] = v;
    }
  }

  const pendingVariableWrites: Array<{ variableId: string; value: unknown }> = [];
  const emailVariableId = initData.emailVariableId ?? null;

  // Variable change listeners
  const variableListeners: Record<string, Array<(event: { variableId: string; oldValue: unknown; newValue: unknown }) => void>> = {};
  const wildcardListeners: Array<(event: { variableId: string; oldValue: unknown; newValue: unknown }) => void> = [];

  // Purchase callbacks
  const purchaseCallbacks: Record<string, { onSuccess: () => void; onCancel: () => void; onError: (err: { error: string }) => void }> = {};
  let activePurchaseRequestId: string | null = null;
  const mountedPurchases = new Map<string, () => void>();
  let mountsDisposed = false;

  // Child sections
  const childSectionsList = initData.childSections ?? null;

  // ── Helpers ────────────────────────────────────────────────

  function fireVariableListeners(variableId: string, oldValue: unknown, newValue: unknown): void {
    const event = { variableId, oldValue, newValue };
    const specific = variableListeners[variableId];
    if (specific) {
      specific.forEach(cb => { try { cb(event); } catch (e) { console.error('[segmentlySDK] listener error:', e); } });
    }
    wildcardListeners.forEach(cb => { try { cb(event); } catch (e) { console.error('[segmentlySDK] listener error:', e); } });
  }

  function getVariableDefinitionById(variableId: string): typeof variableDefinitions[string] | null {
    if (variableDefinitions[variableId]) return variableDefinitions[variableId];
    for (const def of Object.values(variableDefinitions)) {
      if (def && (def.id === variableId || def.name === variableId)) return def;
    }
    return null;
  }

  function getEmailCandidateIds(): string[] {
    const ids: string[] = [];
    if (emailVariableId) ids.push(emailVariableId);
    for (const [vid, def] of Object.entries(variableDefinitions)) {
      // Defensive: initData.variableDefinitions may have undefined entries in
      // edge cases (virtual variables). Use optional chaining so minifier
      // cannot drop the check based on TypeScript's non-nullable typing.
      const name = (def?.name || '').toLowerCase();
      const id = (def?.id || '').toLowerCase();
      if (name === 'email' || id === 'email' || name === 'user_email' || id === 'user_email') {
        if (!ids.includes(vid)) ids.push(vid);
      }
    }
    if (typeof variableValues.email === 'string' && !ids.includes('email')) ids.push('email');
    if (typeof variableValues.user_email === 'string' && !ids.includes('user_email')) ids.push('user_email');
    return ids;
  }

  function findEmailVariableId(): string | null {
    return getEmailCandidateIds()[0] ?? null;
  }

  function findEmailVariableIdForWrite(): string {
    return findEmailVariableId() ?? 'email';
  }

  function lookupByScreenId(screenId: string, fieldPath: string): unknown {
    const userData = initData.userData || {};
    const nested = userData[screenId];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const inputMatch = fieldPath.match(/^input\['(.+?)'\]\.value$/);
      if (inputMatch) return (nested as Record<string, unknown>)[inputMatch[1]];
      if ((nested as Record<string, unknown>)[fieldPath] !== undefined) return (nested as Record<string, unknown>)[fieldPath];
    }
    return userData[screenId + '.' + fieldPath];
  }

  function lookupByScreenName(screenName: string, fieldPath: string): unknown {
    const mapping = (initData as unknown as Record<string, unknown>).screenMapping as Record<string, string> || {};
    let screenId: string | null = null;
    for (const [id, name] of Object.entries(mapping)) {
      if (name === screenName) { screenId = id; break; }
    }
    if (!screenId) return undefined;
    return lookupByScreenId(screenId, fieldPath);
  }

  function lookupScreenUserData(screenIdOrName: string): Record<string, unknown> | null {
    const mapping = (initData as unknown as Record<string, unknown>).screenMapping as Record<string, string> || {};
    const userData = initData.userData || {};
    let targetId = screenIdOrName;
    for (const [id, name] of Object.entries(mapping)) {
      if (name === screenIdOrName) { targetId = id; break; }
    }
    const nested = userData[targetId];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as Record<string, unknown>;
    }
    const results: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(userData)) {
      if (key.startsWith(targetId + '.')) {
        results[key.substring(targetId.length + 1)] = val;
      }
    }
    return Object.keys(results).length > 0 ? results : null;
  }

  function findChildSectionByIdOrLabel(nameOrId: string) {
    return findCustomScreenChildSection(childSectionsList, nameOrId);

  }

  function normalizeOptionActionRequest(
    sectionIdOrLabel: string,
    target: unknown,
  ): CustomScreenOptionActionRequest | null {
    const section = findChildSectionByIdOrLabel(sectionIdOrLabel);
    if (!section?.id) {
      console.warn('[segmentlySDK] triggerOptionAction: child section not found', sectionIdOrLabel);
      return null;
    }

    const request: CustomScreenOptionActionRequest = { sectionId: section.id };
    if (typeof target === 'string' && target.length > 0) {
      request.optionId = target;
    } else if (typeof target === 'number' && Number.isFinite(target)) {
      request.index = Math.floor(target);
    } else if (target && typeof target === 'object') {
      const normalized = target as { optionId?: unknown; index?: unknown };
      if (typeof normalized.optionId === 'string' && normalized.optionId.length > 0) {
        request.optionId = normalized.optionId;
      }
      if (typeof normalized.index === 'number' && Number.isFinite(normalized.index)) {
        request.index = Math.floor(normalized.index);
      }
    }

    if (request.optionId === undefined && request.index === undefined) {
      console.warn('[segmentlySDK] triggerOptionAction: target must be an optionId or index', target);
      return null;
    }

    return request;
  }

  function normalizeButtonActionRequest(
    sectionIdOrLabel: string,
  ): CustomScreenButtonActionRequest | null {
    const section = findChildSectionByIdOrLabel(sectionIdOrLabel);
    if (!section?.id) {
      console.warn('[segmentlySDK] triggerButtonAction: child section not found', sectionIdOrLabel);
      return null;
    }
    if (section.kind !== 'Button') {
      console.warn('[segmentlySDK] triggerButtonAction: child section is not Button', sectionIdOrLabel);
      return null;
    }

    return { sectionId: section.id };
  }

  // Products helper.
  //
  // Accepts an optional `targetProductId` so that multi-ProductCatalog WebEmbeds
  // can resolve the specific catalog containing the requested product. Without
  // this, purchaseProduct('productFromSecondCatalog') would fail because the
  // plain `.find(s.kind === 'ProductCatalog')` always returned catalog #1 and
  // productId lookup inside it would return -1.
  //
  // Backward-compat: callers that omit `targetProductId` (e.g. getSelectedProduct,
  // getProducts) still get the first ProductCatalog, matching the original
  // single-catalog semantics. When `targetProductId` is supplied but no catalog
  // contains it, fall back to the first catalog so the caller can still error
  // with its existing "product not found" message against a defined section.
  function getProductCatalogSection(
    targetProductId?: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): { data: Record<string, unknown>; id: string } | null {
    if (!childSectionsList) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const catalogs = (childSectionsList as any[]).filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (s: any) => s.kind === 'ProductCatalog',
    );
    if (catalogs.length === 0) return null;
    if (!targetProductId) return catalogs[0];
    const match = catalogs.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (s: any) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s.data?.products as any[] | undefined)?.some(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (p: any) => p.productId === targetProductId,
        ),
    );
    return match ?? catalogs[0];
  }

  // ── Build SDK object ───────────────────────────────────────

  const sdk: Record<string, unknown> = {
    // ── Lifecycle ──────────────────────────────────────────
    isReady: () => true, // Shadow DOM SDK is always ready (no async init)
    ready: () => Promise.resolve(sdk),

    // ── Screen data ────────────────────────────────────────
    screen: {
      screenId: initData.screenId,
      screenName: (initData as unknown as Record<string, unknown>).screenName || '',
      labels: initData.labels,
      values: initData.values || {},
      userInputs: initData.userData,
      customData: initData.customData,
      locale: initData.language,
      screenMapping: (initData as unknown as Record<string, unknown>).screenMapping || {},
      customVariableDefinitions: (initData as unknown as Record<string, unknown>).customVariableDefinitions || [],
    },

    // ── Email ──────────────────────────────────────────────
    getEmail: () => {
      for (const eVarId of getEmailCandidateIds()) {
        const val = variableValues[eVarId];
        if (typeof val === 'string') return val;
      }
      return undefined;
    },
    setEmail: (value: string) => {
      const eVarId = findEmailVariableIdForWrite();
      (sdk.setVariable as (id: string, val: unknown) => void)(eVarId, value);
    },

    // ── Platform / Session ─────────────────────────────────
    getPlatformUserId: () => initData.platformUserId || undefined,
    getSessionId: () => (initData.webOnboarding as Record<string, unknown>)?.sessionId || '',
    getCurrentScreenId: () => initData.screenId || '',
    getScreenMapping: () => (initData as unknown as Record<string, unknown>).screenMapping || {},

    // ── User data lookups ──────────────────────────────────
    getScreenUserData: (s: string) => lookupScreenUserData(s),
    getUserDataByScreenId: (sid: string, fp: string) => lookupByScreenId(sid, fp),
    getUserDataByScreenName: (sn: string, fp: string) => lookupByScreenName(sn, fp),
    getCustomData: (k: string) => initData.customData ? (initData.customData as Record<string, unknown>)[k] : undefined,
    getAllCustomData: () => initData.customData || {},
    getCustomVariableDefault: () => undefined,
    getCustomVariableValue: (k: string) => initData.customData ? (initData.customData as Record<string, unknown>)[k] : undefined,
    getCustomVariableDefinitions: () => (initData as unknown as Record<string, unknown>).customVariableDefinitions || [],
    getCustomVariableDefinition: (k: string) => {
      const defs = ((initData as unknown as Record<string, unknown>).customVariableDefinitions || []) as Array<{ key: string }>;
      return defs.find(d => d.key === k) || null;
    },

    // ── V2 Variable System ─────────────────────────────────
    getVariable: (variableId: string) => variableValues[variableId],
    getVariableDefinition: (variableId: string) => getVariableDefinitionById(variableId),
    getVariableDefinitions: () => variableDefinitions,
    getAllVariableValues: () => ({ ...variableValues }),
    setVariable: (variableId: string, value: unknown) => {
      const oldValue = variableValues[variableId];
      variableValues[variableId] = value;
      pendingVariableWrites.push({ variableId, value });
      // Direct callback to host (synchronous — preserves gesture)
      callbacks.onSetVariable?.(variableId, value);
      if (oldValue !== value) {
        fireVariableListeners(variableId, oldValue, value);
      }
    },
    onVariableChange: (variableIdOrStar: string, callback: (event: { variableId: string; oldValue: unknown; newValue: unknown }) => void) => {
      if (typeof callback !== 'function') return () => {};
      if (variableIdOrStar === '*') {
        wildcardListeners.push(callback);
        return () => {
          const idx = wildcardListeners.indexOf(callback);
          if (idx >= 0) wildcardListeners.splice(idx, 1);
        };
      }
      if (!variableListeners[variableIdOrStar]) variableListeners[variableIdOrStar] = [];
      variableListeners[variableIdOrStar].push(callback);
      return () => {
        const arr = variableListeners[variableIdOrStar];
        if (arr) { const idx = arr.indexOf(callback); if (idx >= 0) arr.splice(idx, 1); }
      };
    },
    offVariableChange: (variableIdOrStar: string, callback: (event: unknown) => void) => {
      if (variableIdOrStar === '*') {
        const idx = wildcardListeners.indexOf(callback as typeof wildcardListeners[0]);
        if (idx >= 0) wildcardListeners.splice(idx, 1);
        return;
      }
      const arr = variableListeners[variableIdOrStar];
      if (arr) { const idx = arr.indexOf(callback as typeof arr[0]); if (idx >= 0) arr.splice(idx, 1); }
    },

    // ── Child Sections (Data Sources) ──────────────────────
    //
    // CRITICAL: Sections MUST be addressed by id or label (NEVER by kind), because
    // multiple sections of the same kind can coexist per screen. Kind-based lookup
    // returns a random first match and causes content bleed-through between fields.
    //
    // The ONLY exception is 'ProductCatalog' — the single canonical singleton per
    // screen. All other kinds (Text, Media, BulletList, SingleSelectionList, etc.)
    // require exact id or label match and return null on kind-based lookup.
    getChildSections: () => childSectionsList || [],
    getChildSection: (nameOrId: string) => {
      return findCustomScreenChildSection(childSectionsList, nameOrId, true);
    },
    triggerOptionAction: (sectionIdOrLabel: string, target: unknown) => {
      const request = normalizeOptionActionRequest(sectionIdOrLabel, target);
      if (request) {
        callbacks.onTriggerOptionAction?.(request);
      }
    },
    triggerButtonAction: (sectionIdOrLabel: string) => {
      const request = normalizeButtonActionRequest(sectionIdOrLabel);
      if (request) {
        callbacks.onTriggerButtonAction?.(request);
      }
    },
    getChildSectionText: (nameOrId: string) => {
      if (!childSectionsList || !nameOrId) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const section = findChildSectionByIdOrLabel(nameOrId);
      if (!section || section.kind !== 'Text') return null;
      return { title: section.data.title || '', subtitle: section.data.subtitle || '', subtitle2: section.data.subtitle2 || '' };
    },
    getChildSectionMedia: (nameOrId: string) => {
      if (!childSectionsList || !nameOrId) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const section = findChildSectionByIdOrLabel(nameOrId);
      if (!section || section.kind !== 'Media') return null;
      return { url: section.data.mediaUrl || '', type: section.data.mediaType || '' };
    },

    // ── Products ───────────────────────────────────────────
    getProducts: () => {
      const section = getProductCatalogSection();
      return section ? (section.data.products as unknown[] || []) : [];
    },
    getProductPrice: (productId: string) => {
      // Pass productId so that in multi-catalog WebEmbeds the price lookup
      // targets the catalog that actually owns this product, not always the
      // first one. productPrices is keyed by productId inside the matching
      // section, so returning the wrong section would give `undefined`.
      const section = getProductCatalogSection(productId);
      if (!section) return null;
      const prices = section.data.productPrices as Record<string, unknown> | undefined;
      return prices ? prices[productId] : null;
    },
    getSelectedProduct: () => {
      const section = getProductCatalogSection();
      if (!section) return null;
      const products = section.data.products as Array<Record<string, unknown>> | undefined;
      const idx = section.data.selectedProductIndex as number | undefined;
      if (!products || idx == null) return null;
      return products[idx] || null;
    },
    selectProduct: (productIdOrIndex: string | number) => {
      // When a productId (string) is passed, resolve the specific catalog it
      // belongs to. When an index (number) is passed, fall back to the first
      // catalog — index-based addressing is inherently single-section.
      const section = getProductCatalogSection(
        typeof productIdOrIndex === 'string' ? productIdOrIndex : undefined,
      );
      if (!section?.data?.products) {
        console.warn('[segmentlySDK] selectProduct: no ProductCatalog child section');
        return;
      }
      const products = section.data.products as Array<{ productId?: string; isSelected?: boolean }>;
      let index = -1;
      if (typeof productIdOrIndex === 'number') {
        index = productIdOrIndex;
      } else {
        index = products.findIndex(p => p.productId === productIdOrIndex);
      }
      if (index < 0 || index >= products.length) {
        console.warn('[segmentlySDK] selectProduct: invalid identifier', productIdOrIndex);
        return;
      }
      for (let j = 0; j < products.length; j++) { products[j].isSelected = (j === index); }
      (section.data as Record<string, unknown>).selectedProductIndex = index;
      callbacks.onSelectProduct?.(section.id, index, products[index].productId || '');
    },

    // Optional in-page provider control. Ordinary purchaseProduct stays synchronous.
    mountPurchaseButton: (targetOrSelector: Element | string, productIdOrIndex: string | number, options?: {
      onReady?: () => void; onFallback?: () => void;
      onSuccess?: () => void; onCancel?: () => void; onError?: (err: { error: string }) => void;
    }) => {
      const opts = options || {};
      const fallback = () => { try { opts.onFallback?.(); } catch { /* Host ownership must survive authored callbacks. */ } };
      const noop = Object.assign(() => {}, { requestClose: () => {} });
      if (mountsDisposed || activePurchaseRequestId || !callbacks.onMountPurchaseButton) { fallback(); return noop; }
      let target: Element | null;
      try { target = typeof targetOrSelector === 'string' ? shadowRoot.querySelector(targetOrSelector) : targetOrSelector; }
      catch { fallback(); return noop; }
      const section = getProductCatalogSection(typeof productIdOrIndex === 'string' ? productIdOrIndex : undefined);
      const products = section?.data?.products as Array<{ productId?: string }> | undefined;
      const index = typeof productIdOrIndex === 'number' ? productIdOrIndex : products?.findIndex(p => p.productId === productIdOrIndex);
      const productId = index !== undefined && Number.isSafeInteger(index) && index >= 0 ? products?.[index]?.productId : undefined;
      if (!target || target.getRootNode() !== shadowRoot || !section || !productId) { fallback(); return noop; }
      const requestId = `mount_${crypto.randomUUID()}`;
      let requestClose: (() => void) | undefined;
      let closeBeforeRegistration = false;
      let settled = false;
      const settle = (callback: () => void) => {
        if (settled || mountsDisposed || !shadowRoot.host.isConnected) return;
        settled = true; callback();
      };
      const success = () => settle(() => opts.onSuccess?.());
      purchaseCallbacks[requestId] = { onSuccess: success,
        onCancel: () => settle(() => opts.onCancel?.()),
        onError: error => settle(() => opts.onError?.(error)) };
      let retire: (() => void) | null = null;
      try { retire = callbacks.onMountPurchaseButton(target, { requestId, productId, childSectionId: section.id,
        registerRequestClose: close => {
          requestClose = close;
          if (closeBeforeRegistration) {
            closeBeforeRegistration = false;
            if (!settled && !mountsDisposed && mountedPurchases.has(requestId) &&
              shadowRoot.host.isConnected && target.isConnected && target.getRootNode() === shadowRoot) close();
          }
          return () => { if (requestClose === close) requestClose = undefined; };
        },
        onReady: () => { if (mountedPurchases.has(requestId)) opts.onReady?.(); }, onFallback: fallback,
        onDispose: () => mountedPurchases.get(requestId)?.(),
        // Only the payment owner retains this receiver after visual retirement.
        // It cannot settle or unlock a later ordinary SDK request.
        onRecoveredSuccess: () => {
          if (settled || mountsDisposed || !shadowRoot.host.isConnected) return;
          // Offer replacements are waiting on this payment's owner. Retire them
          // before releasing it, including when authored navigation is asynchronous.
          for (const dispose of [...mountedPurchases.values()]) dispose();
          delete purchaseCallbacks[requestId]; success();
        },
        onRecoveredError: message => {
          if (settled || mountsDisposed || !shadowRoot.host.isConnected) return;
          // Verified unpaid: the waiting offer replacement keeps its slot and may prepare.
          delete purchaseCallbacks[requestId];
          settle(() => opts.onError?.({ error: message }));
        } }); }
      catch { /* Invalid/unsupported hosts keep the author's standard purchase button. */ }
      if (!retire) { delete purchaseCallbacks[requestId]; fallback(); return noop; }
      const dispose = () => {
        if (!mountedPurchases.delete(requestId)) return;
        requestClose = undefined; closeBeforeRegistration = false;
        delete purchaseCallbacks[requestId]; retire?.();
      };
      dispose.requestClose = () => {
        if (!settled && !mountsDisposed && mountedPurchases.get(requestId) === dispose &&
          shadowRoot.host.isConnected && target.isConnected && target.getRootNode() === shadowRoot) {
          if (requestClose) requestClose();
          else closeBeforeRegistration = true;
        }
      };
      mountedPurchases.set(requestId, dispose);
      return dispose;
    },

    // ── Purchase Product ───────────────────────────────────
    // CRITICAL: This is a SYNCHRONOUS call chain — preserves user gesture for Apple Pay!
    purchaseProduct: (productIdOrIndex: string | number, options?: {
      onSuccess?: () => void;
      onCancel?: () => void;
      onError?: (err: { error: string }) => void;
    }) => {
      const opts = options || {};
      const completion = createPurchaseCompletion(opts);
      if (activePurchaseRequestId) {
        console.warn('[segmentlySDK] purchaseProduct: payment already in progress');
        completion.fail({ error: 'Payment already in progress' }, 'PURCHASE_IN_PROGRESS');
        return completion.promise;
      }
      // Multi-catalog aware lookup: when a productId string is supplied,
      // find the specific ProductCatalog section that owns this product so
      // that the subsequent findIndex() below hits the correct products array.
      const section = getProductCatalogSection(
        typeof productIdOrIndex === 'string' ? productIdOrIndex : undefined,
      );
      if (!section?.data?.products) {
        console.warn('[segmentlySDK] purchaseProduct: no ProductCatalog child section');
        completion.fail({ error: 'No products available' }, 'NO_PRODUCTS');
        return completion.promise;
      }
      const products = section.data.products as Array<{ productId?: string }>;
      let index = -1;
      if (typeof productIdOrIndex === 'number') {
        index = productIdOrIndex;
      } else {
        index = products.findIndex(p => p.productId === productIdOrIndex);
      }
      if (index < 0 || index >= products.length) {
        console.warn('[segmentlySDK] purchaseProduct: invalid product', productIdOrIndex);
        completion.fail({ error: 'Product not found' }, 'PRODUCT_NOT_FOUND');
        return completion.promise;
      }

      const requestId = 'pur_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
      activePurchaseRequestId = requestId;
      purchaseCallbacks[requestId] = completion;

      // CRITICAL: Call onPurchaseProduct SYNCHRONOUSLY to preserve user gesture context
      // for Apple Pay's paymentRequest.show(). The entire chain from here through
      // handleEmbedPurchaseProduct → handlePurchaseAction → purchase() → showWalletPayment()
      // → paymentRequest.show() must stay in the same synchronous call stack.
      //
      // selectProduct() triggers onSelectProduct → setProductSelections (React setState)
      // which caused render loops when called in the same sync stack as the purchase callback.
      // Solution: defer selectProduct to after the purchase callback. The purchase handler
      // (handleEmbedPurchaseProduct) already does its own setProductSelections (useEmbedPurchase.ts:95-106),
      // so the selectProduct call here is only for local SDK state consistency.
      callbacks.onPurchaseProduct?.(requestId, products[index].productId || '', section.id);

      // Deferred: update local SDK product selection state (isSelected flags + selectedProductIndex).
      // This triggers onSelectProduct → setState, but since it's deferred via queueMicrotask,
      // it won't interfere with the synchronous purchase/wallet flow above.
      queueMicrotask(() => {
        (sdk.selectProduct as (id: number) => void)(index);
      });
      return completion.promise;
    },

    // ── Action Methods ─────────────────────────────────────
    // CRITICAL: trackEvent must NOT call back to the host analytics manager
    // because the host dispatches events to adapters (including custom scripts),
    // and custom scripts may call segmentlySDK.trackEvent() → infinite recursion.
    // In Shadow DOM mode, trackEvent is a no-op for events that originate FROM
    // the analytics manager. Only user-initiated trackEvent calls go through.
    trackEvent: (() => {
      let isDispatching = false;
      return (name: string, params?: Record<string, unknown>) => {
        if (isDispatching) return; // Break re-entrancy loop
        isDispatching = true;
        try {
          callbacks.onTrackEvent(name, params);
        } finally {
          isDispatching = false;
        }
      };
    })(),
    navigateNext: (args?: Record<string, unknown>) => {
      const data = args || {};
      // V2: write args keys into matching variables before navigating (mirrors iframe SDK)
      for (const [key, value] of Object.entries(data)) {
        if (key.startsWith('_')) continue;
        let varId: string | null = null;
        if (variableDefinitions[key]) {
          varId = key;
        } else {
          for (const [vid, def] of Object.entries(variableDefinitions)) {
            // Optional chaining prevents minifier from dropping the nullish check
            if (def?.name === key || def?.id === key) { varId = vid; break; }
          }
        }
        if (varId) {
          (sdk.setVariable as (id: string, val: unknown) => void)(varId, value);
        }
      }
      // Defense-in-depth: embed pending variable writes
      const enrichedArgs: Record<string, unknown> = { ...data };
      if (pendingVariableWrites.length > 0) {
        enrichedArgs.__pendingVariableWrites = pendingVariableWrites.slice();
        pendingVariableWrites.length = 0;
      }
      callbacks.onNavigate(enrichedArgs);
    },
    navigateBack: () => {
      callbacks.onBack();
    },
    setValue: (key: string, value: unknown) => {
      callbacks.onValueChange?.(key, value);
    },
    setCustomData: (key: string, value: unknown) => {
      if (initData.customData) {
        (initData.customData as Record<string, unknown>)[key] = value;
      }
      callbacks.onValueChange?.('customData.' + key, value);
    },

    debug: false,
  };

  // ── Legacy API ─────────────────────────────────────────────

  // inputs Proxy — bridges legacy reads/writes to V2 variable system
  const inputsProxy = typeof Proxy !== 'undefined' ? new Proxy({} as Record<string, unknown>, {
    get(_target, prop) {
      if (typeof prop !== 'string') return undefined;
      if (variableValues[prop] !== undefined) return variableValues[prop];
      for (const [vid, def] of Object.entries(variableDefinitions)) {
        if (def?.name === prop || def?.id === prop) return variableValues[vid];
      }
      return _target[prop];
    },
    set(_target, prop, value) {
      if (typeof prop !== 'string') return true;
      let targetVarId: string | null = null;
      if (variableDefinitions[prop]) {
        targetVarId = prop;
      } else {
        for (const [vid, def] of Object.entries(variableDefinitions)) {
          if (def?.name === prop || def?.id === prop) { targetVarId = vid; break; }
        }
      }
      if (targetVarId) {
        (sdk.setVariable as (id: string, val: unknown) => void)(targetVarId, value);
      } else {
        _target[prop] = value;
      }
      return true;
    },
    has(_target, prop) {
      if (typeof prop !== 'string') return false;
      if (variableValues[prop] !== undefined) return true;
      for (const [vid, def] of Object.entries(variableDefinitions)) {
        if (def?.name === prop || def?.id === prop) return variableValues[vid] !== undefined;
      }
      return prop in _target;
    },
    ownKeys(_target) {
      const keys = Object.keys(_target);
      for (const vid of Object.keys(variableValues)) {
        if (!keys.includes(vid)) keys.push(vid);
      }
      return keys;
    },
    getOwnPropertyDescriptor(_target, prop) {
      if (typeof prop === 'string' && (variableValues[prop] !== undefined || prop in _target)) {
        return { configurable: true, enumerable: true, value: variableValues[prop] ?? _target[prop] };
      }
      return Object.getOwnPropertyDescriptor(_target, prop);
    },
  }) : {};

  // Set legacy globals
  const w = window as unknown as Record<string, unknown>;
  w.onboardingOnlineCustomScreenData = {
    inputs: inputsProxy,
    labels: initData.labels,
    userData: initData.userData,
    customData: initData.customData,
    webOnboarding: initData.webOnboarding,
    incomeUserData: initData.incomeUserData,
    language: initData.language,
    screenId: initData.screenId,
    platformUserId: initData.platformUserId || undefined,
  };

  // Protect inputs proxy from overwrite by legacy boilerplate
  try {
    Object.defineProperty(w.onboardingOnlineCustomScreenData, 'inputs', {
      get: () => inputsProxy,
      set: () => { /* ignore overwrites */ },
      configurable: true,
      enumerable: true,
    });
  } catch { /* ignore */ }

  // Protect legacy navigation globals from client-HTML overwrite.
  // The onboarding.online legacy boilerplate (widely copied into Custom Screen
  // HTML) top-levels `window.leaveCustomScreen = () => {};` and then reassigns
  // it inside a `window.onmessage` handler that only fires in iframe mode.
  // In shadow-DOM mode that message never arrives, so the no-op stub stays,
  // navigation silently breaks after the user clicks Continue, and the next
  // screen never mounts. Using the same accessor-descriptor pattern as
  // `onboardingOnlineCustomScreenData.inputs` above (ignored setter) keeps
  // client code silent-safe (no TypeError in strict mode) while making the
  // overwrite a no-op. `configurable: true` lets the next screen remount
  // re-define the property for its own SDK instance.
  const leaveCustomScreenFn = (args: Record<string, unknown>) =>
    (sdk.navigateNext as (a: Record<string, unknown>) => void)(args);
  const backScreenFn = () => (sdk.navigateBack as () => void)();
  try {
    Object.defineProperty(w, 'leaveCustomScreen', {
      get: () => leaveCustomScreenFn,
      set: () => { /* silently ignore client overwrite */ },
      configurable: true,
      enumerable: true,
    });
    Object.defineProperty(w, 'backScreen', {
      get: () => backScreenFn,
      set: () => { /* silently ignore client overwrite */ },
      configurable: true,
      enumerable: true,
    });
  } catch {
    // Fallback to plain assignment if defineProperty somehow fails
    w.leaveCustomScreen = leaveCustomScreenFn;
    w.backScreen = backScreenFn;
  }
  w.getEmail = () => (sdk.getEmail as () => string | undefined)();
  w.getPlatformUserId = () => (sdk.getPlatformUserId as () => string | undefined)();

  // Amplitude stub — only install if no real SDK is present.
  // Shadow DOM shares the host window (unlike iframe), so unconditional
  // assignment poisons the host's AmplitudeAdapter — every track() call
  // after a paywall mount would route through this no-op proxy, return
  // void instead of an AmplitudeReturn promise, and events would silently
  // never reach Amplitude. Skipping when a real SDK is loaded means legacy
  // `window.amplitude.track()` calls from custom HTML hit the real SDK
  // directly (bypassing host AnalyticsEventManager and other adapters).
  // Custom HTML that wants multi-adapter routing should use
  // `window.segmentlySDK.trackEvent()` instead.
  const amplitudeProxy = {
    track: (name: string, props?: Record<string, unknown>) => (sdk.trackEvent as (n: string, p?: Record<string, unknown>) => void)(name, props),
    init: () => {},
    identify: () => {},
    setUserId: () => {},
    getInstance: () => amplitudeProxy,
  };
  if (typeof w.amplitude === 'undefined') {
    w.amplitude = amplitudeProxy;
  }

  // Shadow DOM helpers on SDK (for DOM queries inside shadow root)
  sdk._shadowRoot = shadowRoot;
  sdk.getShadowRoot = () => shadowRoot;
  sdk.getElementById = (id: string) => shadowRoot.getElementById(id);
  sdk.querySelector = (sel: string) => shadowRoot.querySelector(sel);
  sdk.querySelectorAll = (sel: string) => shadowRoot.querySelectorAll(sel);

  // ── Return instance with delivery methods ──────────────────

  return {
    sdk,
    _disposePurchaseSlots() { mountsDisposed = true; for (const dispose of [...mountedPurchases.values()]) dispose(); },
    _deliverPurchaseResult(requestId: string, status: 'success' | 'cancel' | 'error', error?: string) {
      if (status !== 'success' && status !== 'cancel' && status !== 'error') return;
      const cbs = purchaseCallbacks[requestId];
      if (!cbs) return;
      delete purchaseCallbacks[requestId];
      // A late/duplicate or mounted-slot result cannot unlock another SDK request.
      // Release before invoking the author callback so synchronous retry stays valid.
      if (activePurchaseRequestId === requestId) activePurchaseRequestId = null;
      mountedPurchases.get(requestId)?.();
      try {
        if (status === 'success') cbs.onSuccess();
        else if (status === 'cancel') cbs.onCancel();
        else if (status === 'error') cbs.onError({ error: error || 'Unknown error' });
      } catch (e) {
        console.error('[segmentlySDK] purchaseProduct callback error:', e);
      }
    },
    _deliverVariableUpdate(variableId: string, value: unknown) {
      const oldVal = variableValues[variableId];
      // Dedup: skip if value unchanged
      if (oldVal === value) return;
      if (Array.isArray(oldVal) && Array.isArray(value) &&
          oldVal.length === value.length && oldVal.every((v, i) => v === (value as unknown[])[i])) return;
      variableValues[variableId] = value;
      fireVariableListeners(variableId, oldVal, value);
    },
  };
}
